import { useEffect, useMemo } from 'react';
import { rankWith, ControlProps, isControl, JsonSchema, JsonFormsRendererRegistryEntry, JsonFormsCellRendererRegistryEntry } from '@jsonforms/core';
import { withJsonFormsControlProps, JsonFormsDispatch, useJsonForms } from '@jsonforms/react';
import { DelegatedControlContext, delegateTo } from '@/components/forms/controlMarks';
import { useDimensionDefinitions } from '@/hooks/finance/useDimensionDefinition';
import { supportsHierarchyChildren } from '@/finance/planEntryRows';

const DEFAULT_ENUMERATION = 'ALL_ACTIVE_VALUES';
const HIERARCHY_ENUMERATION = 'HIERARCHY_CHILDREN';

/**
 * Gates a PlanViewRowDimension row-enumeration control (`rowEnumeration`, `rootRef`, `filterJson`)
 * on the axis's row source. Three axes get the config:
 *  - a DIMENSION axis whose selected `dimensionDefinitionId` is an ENTITY_BACKED dimension (Sortiment,
 *    Kund, …) — all three controls apply, but `rootRef` only for a HIERARCHY_CHILDREN axis on a
 *    HIERARCHICAL backing (Sortiment/ProductGroup). A flat backing (Kund, Affärsform, Leverantör) has
 *    no tree: HIERARCHY_CHILDREN is dropped from the dropdown, exactly as for a free dimension, and the
 *    root picker never shows — it is hard-wired to the ProductGroup tree (`hierarchyKey` on the DSL
 *    field), so offering it there would ask the author to root a Trade Type axis in a product group;
 *  - an ACCOUNT axis (`rowSourceKind = ACCOUNT` — rows are GL accounts) — `rowEnumeration`
 *    (ALL_ACTIVE_VALUES / FILTERED / HIERARCHY_CHILDREN), `filterJson`, and — for HIERARCHY_CHILDREN —
 *    `accountGroupRootRef` (the AccountGroup subtree root) apply, but never `rootRef` (that is the
 *    ProductGroup tree, wired for the DIMENSION case only);
 *  - a DIMENSION axis on a FREE dimension (Cost Center, Department, …) — `rowEnumeration` and
 *    `filterJson` apply, filtering the dimension's own DimensionValue attributes (code, name,
 *    description, external code), but NEITHER root picker: a free dimension's values carry no
 *    hierarchy this dialog can walk, so its enumeration is narrowed to ALL_ACTIVE_VALUES / FILTERED
 *    (see `withoutHierarchyChildren`) and HIERARCHY_CHILDREN is never offered — the same narrowing a
 *    flat entity-backed dimension gets.
 *
 * The condition depends on the *referenced* dimension's `valueSource` (not a value in the form), so
 * it can't be expressed as a static JSONForms `visibleWhen` rule — it reads the live dimension list.
 * When visible, the control is delegated to the next-best renderer (the hierarchy tree picker,
 * the enum dropdown, the condition editor) via JsonFormsDispatch with this gate excluded from the set.
 */
const GATED_PROPS = new Set(['rowEnumeration', 'rootRef', 'filterJson', 'accountGroupRootRef']);

function scopeProperty(uischema: any): string | undefined {
  const scope: string | undefined = uischema?.scope;
  return scope ? scope.split('/').pop() : undefined;
}

/**
 * The same schema with HIERARCHY_CHILDREN removed from `rowEnumeration` — the parallel `enum` /
 * `enumNames` / `enumOrdinals` arrays the static-enum renderer reads are all sliced at the same index,
 * so the remaining options keep their labels. Returns the schema untouched when there is nothing to
 * drop, so the memo above keeps a stable reference for the non-free case.
 */
/** The generated enum schema the static-enum renderer reads: `enum` plus its parallel label arrays. */
type EnumPropertySchema = { enum?: string[]; enumNames?: string[]; enumOrdinals?: number[] };

function withoutHierarchyChildren(rootSchema: JsonSchema): JsonSchema {
  const properties = rootSchema?.properties as Record<string, EnumPropertySchema> | undefined;
  const enumeration = properties?.rowEnumeration;
  if (!enumeration?.enum) return rootSchema;

  const dropAt = enumeration.enum.indexOf(HIERARCHY_ENUMERATION);
  if (dropAt < 0) return rootSchema;

  const keep = <T,>(list: T[] | undefined) => list?.filter((_, index) => index !== dropAt);

  return {
    ...rootSchema,
    properties: {
      ...properties,
      rowEnumeration: {
        ...enumeration,
        enum: keep(enumeration.enum),
        enumNames: keep(enumeration.enumNames),
        enumOrdinals: keep(enumeration.enumOrdinals),
      },
    },
  } as unknown as JsonSchema;
}

interface GateProps extends ControlProps {
  renderers?: JsonFormsRendererRegistryEntry[];
  cells?: JsonFormsCellRendererRegistryEntry[];
}

export const EntityBackedRowConfigComponent = (props: GateProps) => {
  const { uischema, rootSchema, path, data, handleChange, visible, enabled, renderers, cells } = props;
  const ctx = useJsonForms();
  const { data: definitions } = useDimensionDefinitions();

  const prop = scopeProperty(uischema);
  const selectedId = (ctx.core?.data as any)?.dimensionDefinitionId as string | undefined;
  const rowEnumeration = (ctx.core?.data as any)?.rowEnumeration as string | undefined;
  const isAccount = (ctx.core?.data as any)?.rowSourceKind === 'ACCOUNT';
  // An ACCOUNT axis needs no dimension lookup — its kind is in the form data itself. For a DIMENSION
  // axis the dimension list must be loaded before we can know the kind — until then, don't mutate or
  // hide permanently (avoid clearing a saved rootRef on an entity-backed row while the list loads).
  const known = isAccount || (!!definitions && !!selectedId);
  const selected = !isAccount && known ? definitions!.find(d => d.id === selectedId) : undefined;
  const isEntityBacked = selected?.valueSource === 'ENTITY_BACKED';
  const isFree = selected?.valueSource === 'FREE';
  const showsConfig = isAccount || isEntityBacked || isFree;
  // Only a hierarchical backing (ProductGroup) — or the ACCOUNT axis, rooted in the AccountGroup tree —
  // can enumerate HIERARCHY_CHILDREN. A flat backing (Customer, Trade Type, Supplier) has no tree, so it
  // is narrowed exactly like a free dimension.
  const isHierarchyBacked = supportsHierarchyChildren(selected?.backingEntity, selected?.valueSource);
  const hierarchyCapable = isAccount || isHierarchyBacked;

  // Keep the stored config consistent with the axis's source kind:
  //  - rowEnumeration is non-null: default it to ALL_ACTIVE_VALUES so a hidden control never fails its
  //    required validation. A hierarchy-capable axis (ACCOUNT, or a dimension on a hierarchical backing)
  //    supports all three enumerations, so only null/empty is defaulted there; on every other axis that
  //    shows the config — a free dimension, or one on a FLAT backing — HIERARCHY_CHILDREN is coerced away
  //    too (there is no tree to root it in, so the axis would enumerate the wrong rows or none), and a
  //    dimension that shows no config at all is forced to the default outright;
  //  - rootRef (ProductGroup tree) only applies to a HIERARCHY_CHILDREN axis on a hierarchical backing;
  //    clear it otherwise (incl. a leftover after a switch to ACCOUNT, to a free dimension, or to a flat
  //    entity-backed one such as Trade Type);
  //  - accountGroupRootRef (AccountGroup tree) only applies to an ACCOUNT + HIERARCHY_CHILDREN axis; clear
  //    it otherwise (incl. a leftover after switching the enumeration or the source kind);
  //  - filterJson applies to an entity-backed, account or free axis — i.e. every axis that shows the
  //    config — so it is only cleared when the config is hidden altogether.
  useEffect(() => {
    if (!known) return;
    if (prop === 'rowEnumeration') {
      if (hierarchyCapable) {
        if (data == null || data === '') handleChange(path, DEFAULT_ENUMERATION);
      } else if (showsConfig) {
        // Neither a free dimension's values nor a flat backing's records carry a hierarchy this dialog
        // can walk, so HIERARCHY_CHILDREN would enumerate the wrong rows (the picker is the ProductGroup
        // tree) or none; ALL_ACTIVE_VALUES and FILTERED are the two enumerations left.
        if (data == null || data === '' || data === HIERARCHY_ENUMERATION) handleChange(path, DEFAULT_ENUMERATION);
      } else if (data !== DEFAULT_ENUMERATION) {
        handleChange(path, DEFAULT_ENUMERATION);
      }
    } else if (prop === 'rootRef') {
      const needsRoot = isHierarchyBacked && rowEnumeration === HIERARCHY_ENUMERATION;
      if (!needsRoot && data != null && data !== '') handleChange(path, undefined);
    } else if (prop === 'accountGroupRootRef') {
      const needsAccountRoot = isAccount && rowEnumeration === 'HIERARCHY_CHILDREN';
      if (!needsAccountRoot && data != null && data !== '') handleChange(path, undefined);
    } else if (!showsConfig) {
      if (data != null && data !== '') handleChange(path, undefined);
    }
  }, [known, prop, isAccount, isHierarchyBacked, hierarchyCapable, showsConfig, rowEnumeration, data, path, handleChange]);

  // A row axis with no tree behind it — a free dimension, or one on a flat backing (Kund, Affärsform,
  // Leverantör) — offers ALL_ACTIVE_VALUES / FILTERED only: HIERARCHY_CHILDREN is dropped from the enum
  // the dropdown renders from, so it cannot be picked in the first place (the effect above is the guard
  // for a value already stored). This narrows the schema handed to the dispatch, never the form's own:
  // validation must still accept every value the entity declares.
  const dispatchSchema = useMemo(
    () => (hierarchyCapable ? rootSchema : withoutHierarchyChildren(rootSchema)),
    [hierarchyCapable, rootSchema],
  );

  if (!showsConfig) {
    return null;
  }
  // RootRef is the ProductGroup tree picker — only for a HIERARCHY_CHILDREN (DIMENSION) axis on a
  // hierarchical backing, never for a flat one (a Trade Type axis has no product-group root to pick) and
  // never for an ACCOUNT axis (its counterpart is accountGroupRootRef below).
  if (prop === 'rootRef' && (!isHierarchyBacked || rowEnumeration !== HIERARCHY_ENUMERATION)) {
    return null;
  }
  // AccountGroupRootRef is the AccountGroup tree picker — only for an ACCOUNT + HIERARCHY_CHILDREN axis.
  if (prop === 'accountGroupRootRef' && !(isAccount && rowEnumeration === HIERARCHY_ENUMERATION)) {
    return null;
  }

  // Delegate to the real renderer for this control, with this gate removed from the set so the
  // dispatch can't re-select itself (infinite recursion) — by its tester, which a form's control
  // marks keep while they wrap the renderer component (controlMarks).
  const available = renderers ?? ctx.renderers ?? [];
  const delegated = delegateTo(available, entityBackedRowConfigTester);

  // JsonFormsDispatch composes the control's data path as composePaths(path, dataPath(scope)).
  // `path` here is already this control's OWN resolved path (e.g. "rowEnumeration"), so it must be
  // passed the PARENT path — otherwise the scope is appended twice and the value is written to a
  // nested object (e.g. data.rowEnumeration = { rowEnumeration: "..." }), failing validation.
  // Likewise pass the ROOT schema so the scope resolves to the real (enum/hierarchy) sub-schema.
  const parentPath = path.includes('.') ? path.slice(0, path.lastIndexOf('.')) : '';
  return (
    <DelegatedControlContext.Provider value={path}>
      <JsonFormsDispatch
        uischema={uischema}
        schema={dispatchSchema}
        path={parentPath}
        enabled={enabled}
        visible={visible}
        renderers={delegated}
        cells={cells ?? ctx.cells}
      />
    </DelegatedControlContext.Provider>
  );
};

export const EntityBackedRowConfigRenderer = withJsonFormsControlProps(EntityBackedRowConfigComponent);

/**
 * Tester — rank above the default control renderers (hierarchy-ref is 10), but only for the
 * `rowEnumeration` / `rootRef` / `filterJson` controls of the PlanViewRowDimension form. The
 * rootSchema guard (`rowEnumeration` is the `RowEnumeration` enum) keeps this from matching another
 * entity's `filterJson` (e.g. AccountDerivationRule).
 */
export const entityBackedRowConfigTester = rankWith(25, (uischema, _schema, context) => {
  if (!isControl(uischema)) return false;
  const prop = scopeProperty(uischema);
  if (!prop || !GATED_PROPS.has(prop)) return false;
  const rootProps = (context?.rootSchema as any)?.properties;
  return rootProps?.rowEnumeration?.enumType === 'RowEnumeration'
    && !!rootProps?.dimensionDefinitionId;
});

export const entityBackedRowConfigEntry = {
  tester: entityBackedRowConfigTester,
  renderer: EntityBackedRowConfigRenderer,
};
